import { getSearchFilters } from '@internal/services/apiClient';
import {
    getAssignedSalesManagers,
    ZohoUserAssignmentPageKey,
} from '@internal/services/TSalesManager';
import { isCanceledAxiosError } from '@internal/utils/api/axios';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { isLeasingQualifiedCountry } from '@internal/utils/machine/leasing';
import { MachineType } from '@internal/utils/machine/MachineType';
import { omitUndefined } from '@internal/utils/omitUndefined';
import { useRefState } from '@internal/utils/useRefState';
import { UserContext } from '@internal/utils/user/UserContext';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button, MobileOverlay } from '@renderer-ui-library/atoms';
import {
    DynamicScrollToTopButton,
    FilterMenuBar,
} from '@renderer-ui-library/molecules';
import { SearchResultsTitle } from '@renderer-ui-library/molecules/SearchResultsTitle/SearchResultsTitle';
import { Box, Divider, Grid } from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import {
    CustomerMap,
    LeasingQualifiedContext,
    LeasingQualifiedFilter,
    SearchFilterBar,
} from '@renderer-ui-library/organisms';
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper';
import { BlockProps } from 'blocks/BlockProps';
import classNames from 'classnames';
import { translations } from 'i18n/translations';
import { isEqual } from 'lodash';
import { useTranslation } from 'next-i18next';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { SalesManagerContext } from 'utils/SalesManager/SalesManagerContext';
import { tracker } from 'utils/tracking/tracker';
import { Cards } from '../CardBannerBlock/Cards';
import { CompareMachinesAlerts } from './CompareMachines/CompareMachinesAlerts';
import { CompareMachinesOverlay } from './CompareMachines/CompareMachinesOverlay';
import { MachineComparisonContextProvider } from './CompareMachines/MachineComparisonContextProvider';
import { CompareMachinesButton } from './CompareMachinesButton/CompareMachinesButton';
import { defaultSearchFilters } from './displayDefinitionToSearchFiltersConfiguration';
import { FilterChips } from './FilterChips';
import { GhostResultsList } from './GhostResultsList';
import { ISearchResultsBlockData } from './ISearchResultsBlockData';
import { LoaderOverlay } from './LoaderOverlay/LoaderOverlay';
import { loadChunkSize } from './loadChunkSize';
import {
    aboveMaxSearchResultsLabel,
    maxSearchResults,
} from './maxSearchResults';
import { NoResults } from './NoResults';
import { ResultsList, SearchResultsListEntry } from './ResultsList';
import { SearchParamsContext } from './SearchParamsContext';
import { SearchResultsSorting } from './SearchResultsSorting';
import styles from './searchResultsBlock.module.scss';
import { TFiltersConfiguration } from './TFiltersConfiguration';
import { TSearchParams } from './TSearchParams';
import { TServerSideSearchResultsBlockProps } from './TServerSideSearchResultsBlockProps';
import {
    getSearchParamsCount,
    sanitizeSearchParams,
    useSearchParams,
} from './useSearchParams';
import { useSearchResults } from './useSearchResults';
import { useSearchResultsRouting } from './useSearchResultsRouting';

type TMobileMenuContent = 'filter' | 'sort' | undefined;
export type SearchResultsBlockProps = BlockProps<
    ISearchResultsBlockData,
    TServerSideSearchResultsBlockProps
>;

export const SearchResultsBlock: React.FC<SearchResultsBlockProps> = React.memo(
    props => {
        const searchParams = useSearchParams();
        const searchParamsRef = useRef<TSearchParams>(searchParams);
        searchParamsRef.current = searchParams;

        const searchParamsCount = useMemo(
            () => getSearchParamsCount(searchParams),
            [searchParams],
        );
        const hasAnySearchParams = searchParamsCount > 0;
        const { machines, resultsCount, isLoading, isServingFromCache } =
            useSearchResults();
        const { salesManagers } = useContext(SalesManagerContext);
        const salesManager = useMemo(
            () =>
                getAssignedSalesManagers(
                    salesManagers,
                    ZohoUserAssignmentPageKey.ProductDetails,
                )[0] ?? null,
            [salesManagers],
        );
        const { t } = useTranslation(localeNamespace.common);
        const { gotoSearchResults } = useSearchResultsRouting();
        const [mobileMenuContent, setMobileMenuContent] =
            useState<TMobileMenuContent>();
        const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
        const { user } = useContext(UserContext);
        const showLeasingFilter = isLeasingQualifiedCountry(user.countryCode);
        const { isMobile } = useMediaQuery();
        const [searchFilters, setSearchFilters, searchFiltersRef] = useRefState<
            Partial<Record<MachineType, TFiltersConfiguration>>
        >(
            props.preFetchedBlockProps.filters
                ? {
                      [props.preFetchedBlockProps.filters.machineType]:
                          props.preFetchedBlockProps.filters.filters,
                  }
                : {},
        );

        const currentSearchFilters = useMemo(() => {
            const filters = searchParams.machineType
                ? searchFilters[searchParams.machineType]
                : null;
            return {
                isLoading: Boolean(searchParams.machineType && !filters),
                filters: filters ?? defaultSearchFilters,
            };
        }, [searchParams.machineType, searchFilters]);

        // when machine type changes and we got the filter settings, ensure we only have filters set that are in the UI
        useEffect(() => {
            const filters = searchParams.machineType
                ? searchFilters[searchParams.machineType]
                : undefined;
            if (filters) {
                const sanitizedSearchParams = sanitizeSearchParams(
                    searchParamsRef.current,
                    filters,
                );
                if (
                    !isEqual(
                        omitUndefined(searchParamsRef.current),
                        omitUndefined(sanitizedSearchParams),
                    )
                ) {
                    gotoSearchResults({
                        ...sanitizedSearchParams,
                        amount: undefined,
                        replace: true,
                    });
                }
            }
        }, [searchParams.machineType, searchFilters, gotoSearchResults]);

        // handle loading of search filters
        useEffect(() => {
            const abortController = new AbortController();
            const machineType = searchParams.machineType;
            if (machineType && !searchFiltersRef.current[machineType]) {
                void getSearchFilters(
                    {
                        machineType,
                        locale: props.locale,
                    },
                    abortController.signal,
                )
                    .catch(err =>
                        isCanceledAxiosError(err) ? null : defaultSearchFilters,
                    )
                    .then(filters => {
                        if (!filters) {
                            return;
                        }
                        setSearchFilters(prev => ({
                            ...prev,
                            [machineType]: filters,
                        }));
                    });

                return () => {
                    abortController.abort();
                };
            }
        }, [
            searchParams.machineType,
            props.locale,
            searchFiltersRef,
            setSearchFilters,
        ]);

        const handleLoadMoreClick = useCallback(
            () =>
                gotoSearchResults({
                    ...searchParams,
                    amount: machines.length + loadChunkSize,
                    replace: true,
                }),
            [gotoSearchResults, searchParams, machines.length],
        );

        useEffect(() => {
            if (!hasLoadedOnce && (!isLoading || isServingFromCache)) {
                setHasLoadedOnce(true);
            }
        }, [isLoading, hasLoadedOnce, isServingFromCache]);

        // track impressions of all listed machines
        useEffect(() => {
            tracker.trackEvent({
                name: 'ProductImpressions',
                action: 'listing-tag',
                ecommerce: {
                    impressions: machines.map(({ data }) => ({
                        id: data.MACHINE_GROUP_ID.toLowerCase(),
                        category: data.MACHINE_TYPE,
                    })),
                },
            });
        }, [machines]);

        const showGhostCards =
            !hasLoadedOnce && isLoading && machines.length === 0;
        const showNoResultsTips =
            hasAnySearchParams && !isLoading && machines.length === 0;
        const isEnabled = !!(
            searchParams.machineType &&
            !isLoading &&
            machines.length > 0
        );
        const showLoadMoreButton = machines.length < resultsCount;

        const scrollUp = useCallback(() => {
            if (document.scrollingElement) {
                document.scrollingElement.scrollTop = 0;
            }
        }, []);

        // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
        const handleFilterClick = useCallback(
            () => setMobileMenuContent('filter'),
            [setMobileMenuContent],
        );
        // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
        const handleSortClick = useCallback(
            () => setMobileMenuContent('sort'),
            [setMobileMenuContent],
        );

        // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
        const closeMobileMenu = useCallback(
            () => setMobileMenuContent(undefined),
            [setMobileMenuContent],
        );

        const handleApplyClick = useCallback(() => {
            scrollUp();
            closeMobileMenu();
        }, [closeMobileMenu, scrollUp]);

        const handleResetClick = useCallback(() => {
            scrollUp();
            gotoSearchResults({
                machineType: searchParams.machineType,
                sort: searchParams.sort,
            });
            closeMobileMenu();
        }, [
            scrollUp,
            gotoSearchResults,
            searchParams.machineType,
            searchParams.sort,
            closeMobileMenu,
        ]);

        const [
            isLeasingQualifiedFilterEnabled,
            setLeasingQualifiedFilterEnabled,
        ] = useState(false);

        const toggleLeasingQualifiedFilter = useCallback(() => {
            setLeasingQualifiedFilterEnabled(prev => !prev);
        }, []);

        const entryChunks = useMemo(() => {
            const leasingAvailable = isLeasingQualifiedCountry(
                user.countryCode,
            );

            const entries: SearchResultsListEntry[] = (
                isLeasingQualifiedFilterEnabled && user.countryCode
                    ? // biome-ignore lint/style/noNonNullAssertion: legacy
                      machines.filter(m => !!m.financing[user.countryCode!])
                    : machines
            ).map(machine => ({
                type: 'machine',
                machine,
            }));

            for (const service of props.data.services) {
                if (service.serviceType === 'leasing' && !leasingAvailable) {
                    continue;
                }

                if (service.repeat && service.repeatPosition) {
                    let index = 1;
                    let position = service.initialPosition - 1;

                    while (position < entries.length) {
                        entries.splice(position, 0, {
                            type: 'service',
                            service,
                        });
                        position =
                            service.repeatPosition * index +
                            service.initialPosition -
                            1;
                        index += 1;
                    }
                } else if (service.initialPosition < machines.length) {
                    entries.splice(service.initialPosition - 1, 0, {
                        type: 'service',
                        service,
                    });
                }
            }

            return {
                one: entries.slice(0, 6),
                two: entries.slice(6, 12),
                three: entries.slice(12),
            };
        }, [
            isLeasingQualifiedFilterEnabled,
            machines,
            props.data.services,
            user.countryCode,
        ]);

        return (
            <LeasingQualifiedContext.Provider
                value={{
                    toggleLeasingQualifiedFilter,
                    isLeasingQualifiedFilterEnabled,
                }}
            >
                <SearchParamsContext.Provider value={{ searchParams }}>
                    <MachineComparisonContextProvider
                        machineType={searchParams.machineType}
                    >
                        <BlockWrapper blockData={props.data} noSpacingTop>
                            <Grid container columnSpacing={2}>
                                <Grid item xs={12}>
                                    <SearchResultsTitle
                                        resultsCount={resultsCount}
                                        popularLinks={
                                            props.preFetchedBlockProps
                                                .popularLinks
                                        }
                                        hasLoadedOnce={hasLoadedOnce}
                                        hasAnySearchParams={hasAnySearchParams}
                                    />
                                </Grid>
                                <Grid item xs={false} md={3}>
                                    <SearchFilterBar
                                        type="desktop"
                                        config={currentSearchFilters.filters}
                                        machineType={searchParams.machineType}
                                        locale={props.locale}
                                        isLoading={
                                            currentSearchFilters.isLoading
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    {hasAnySearchParams && (
                                        <>
                                            {!isMobile && (
                                                <FilterChips
                                                    className={
                                                        styles.desktopChipsWrapper
                                                    }
                                                    filtersConfiguration={
                                                        currentSearchFilters.filters
                                                    }
                                                    locale={props.locale}
                                                    searchParams={searchParams}
                                                    limit={10}
                                                />
                                            )}
                                            <div className={styles.sort}>
                                                <SearchResultsSorting
                                                    closeMobileMenu={
                                                        closeMobileMenu
                                                    }
                                                />
                                                <div
                                                    className={
                                                        styles.sortWrapper
                                                    }
                                                >
                                                    {showLeasingFilter && (
                                                        <LeasingQualifiedFilter />
                                                    )}
                                                    <CompareMachinesButton
                                                        enabled={isEnabled}
                                                        machineType={
                                                            searchParams.machineType
                                                        }
                                                    />

                                                    {isEnabled && (
                                                        <CompareMachinesOverlay
                                                            locale={
                                                                props.locale
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className={styles.resultsWrapper}>
                                        <Grid
                                            container
                                            columnSpacing={2}
                                            rowSpacing={{
                                                xs: 3,
                                                lg: 4,
                                            }}
                                            className={styles.results}
                                        >
                                            <FilterMenuBar
                                                onFilterClick={
                                                    handleFilterClick
                                                }
                                                onSortClick={handleSortClick}
                                                machineType={
                                                    searchParams.machineType
                                                }
                                                showLeasingFilter={
                                                    showLeasingFilter
                                                }
                                                searchParamsCount={
                                                    searchParamsCount
                                                }
                                            />
                                            {showNoResultsTips && (
                                                <NoResults
                                                    salesManager={salesManager}
                                                    machineType={
                                                        searchParams.machineType
                                                    }
                                                    locale={props.locale}
                                                />
                                            )}
                                            {showGhostCards && (
                                                <GhostResultsList
                                                    amount={searchParams.amount}
                                                />
                                            )}
                                            {entryChunks.one.length > 0 && (
                                                <ResultsList
                                                    entries={entryChunks.one}
                                                />
                                            )}
                                            {entryChunks.two.length > 0 && (
                                                <>
                                                    {!showGhostCards &&
                                                        !showNoResultsTips &&
                                                        props.data
                                                            .serviceCards && (
                                                            <Grid item xs={12}>
                                                                <Cards
                                                                    headline={
                                                                        props
                                                                            .data
                                                                            .serviceCards
                                                                            .headline
                                                                    }
                                                                    image={
                                                                        props
                                                                            .data
                                                                            .serviceCards
                                                                            .image
                                                                    }
                                                                    cards={
                                                                        props
                                                                            .data
                                                                            .serviceCards
                                                                            .cards
                                                                    }
                                                                    border
                                                                    initialOpen
                                                                    collapsible={
                                                                        false
                                                                    }
                                                                />
                                                            </Grid>
                                                        )}
                                                    <ResultsList
                                                        entries={
                                                            entryChunks.two
                                                        }
                                                    />
                                                </>
                                            )}

                                            {entryChunks.three.length > 0 && (
                                                <>
                                                    {!showGhostCards &&
                                                        !showNoResultsTips && (
                                                            <Grid item xs={12}>
                                                                <CustomerMap
                                                                    locale={
                                                                        props.locale
                                                                    }
                                                                    mapWidth={
                                                                        isMobile
                                                                            ? 600
                                                                            : 876
                                                                    }
                                                                />
                                                            </Grid>
                                                        )}

                                                    <ResultsList
                                                        entries={
                                                            entryChunks.three
                                                        }
                                                    />
                                                </>
                                            )}
                                            <LoaderOverlay
                                                className={styles.loaderOverlay}
                                                show={
                                                    isLoading &&
                                                    !isServingFromCache
                                                }
                                            />
                                        </Grid>
                                    </div>
                                    {hasAnySearchParams && <Divider />}
                                    {showLoadMoreButton && (
                                        <Box className={styles.moreButton}>
                                            <Button
                                                onClick={handleLoadMoreClick}
                                                variant="contained"
                                                size="large"
                                                endIcon={<KeyboardArrowDown />}
                                                disabled={isLoading}
                                                color="secondary"
                                            >
                                                {t(
                                                    translations.loadMoreMachinesButtonLabel,
                                                )}
                                            </Button>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                            <div
                                className={classNames(styles.mobileFilterBar, {
                                    [styles.mobileFilterBarOpen]:
                                        !!mobileMenuContent,
                                })}
                            >
                                <MobileOverlay
                                    open={!!mobileMenuContent}
                                    title={
                                        mobileMenuContent === 'filter'
                                            ? t(
                                                  translations.searchResultsFiltersTitle,
                                              )
                                            : t(
                                                  translations.searchResultsSortTitle,
                                              )
                                    }
                                    onCloseClick={closeMobileMenu}
                                >
                                    {mobileMenuContent === 'filter' ? (
                                        <SearchFilterBar
                                            type="mobile"
                                            config={
                                                currentSearchFilters.filters
                                            }
                                            machineType={
                                                searchParams.machineType
                                            }
                                            onClickApply={handleApplyClick}
                                            onClickReset={handleResetClick}
                                            locale={props.locale}
                                            isLoading={
                                                currentSearchFilters.isLoading
                                            }
                                        />
                                    ) : (
                                        <SearchResultsSorting
                                            onChange={scrollUp}
                                            closeMobileMenu={closeMobileMenu}
                                        />
                                    )}
                                </MobileOverlay>
                            </div>
                            <DynamicScrollToTopButton
                                label={
                                    hasLoadedOnce
                                        ? `${t(
                                              translations.searchResultsScrollToTopButtonLabel,
                                              {
                                                  count: resultsCount,
                                                  amount:
                                                      resultsCount >=
                                                      maxSearchResults
                                                          ? aboveMaxSearchResultsLabel
                                                          : resultsCount,
                                              },
                                          )}`
                                        : undefined
                                }
                            />
                            <CompareMachinesAlerts locale={props.locale} />
                        </BlockWrapper>
                    </MachineComparisonContextProvider>
                </SearchParamsContext.Provider>
            </LeasingQualifiedContext.Provider>
        );
    },
);

SearchResultsBlock.displayName = 'SearchResultsBlock';
